// src/pages/stock/EsimBulkUpdate.tsx
//
// EsimBulkUpdate
// - Bulk update entry page for inventory records (status / expiration).
// - Uses AppPageHeader slot injection ONLY for the "Download Update Template" action.
// - Shows update runs history table with pagination and delete (soft delete UI via isDeleted).
// - Navigation rule: navigate ONLY when clicking the Index cell (Upload UX consistency).

import { useCallback, useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Chip,
  IconButton,
  TableCell,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';

import DragDropFile from '@components/common/DragDropFile';
import { usePageHeader } from '@components/common/pageHeader.context';

import { downloadFile } from '@utils/file';
import { timestampToDate } from '@utils/time';
import { ESIM_BULK_UPDATE_TEMPLATE } from '@constants/templates';

import {
  deleteUpdateHistory,
  type GetUpdateHistoriesResponse,
  updateFile,
} from '@apis/esimBulk/update';

import { useUpdateHistories } from '@queries/esimBulk/useUpdateHistories';
import { useUpdateHistoriesCount } from '@queries/esimBulk/useUpdateHistoriesCount';

import { Status } from '@models/esim';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

type StatusChipSpec = {
  label: string;
  color: 'default' | 'success' | 'primary' | 'warning';
  variant: 'filled' | 'outlined';
};

const getStatusChipSpec = (
  status: Status | string,
  isDeleted: boolean,
): StatusChipSpec => {
  if (isDeleted) return { label: 'Deleted', color: 'default', variant: 'filled' };

  switch (status) {
    case Status.COMPLETED:
      return { label: 'Completed', color: 'success', variant: 'filled' };
    case Status.PROCESSING:
      return { label: 'Processing', color: 'primary', variant: 'outlined' };
    case Status.PENDING:
      return { label: 'Waiting', color: 'warning', variant: 'filled' };
    default:
      return { label: String(status || 'Unknown'), color: 'default', variant: 'outlined' };
  }
};

export const EsimBulkUpdate = () => {
  const navigate = useNavigate();
  const { setPageHeaderSlots } = usePageHeader();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const [page, setPage] = useState(1);

  const {
    data: historiesData,
    refetch: refetchHistories,
    isLoading: isHistoriesLoading,
  } = useUpdateHistories({ page });

  const {
    data: totalCount,
    refetch: refetchCount,
    isLoading: isCountLoading,
  } = useUpdateHistoriesCount();

  const rows = historiesData?.data ?? [];

  const totalPages = useMemo(() => {
    const count = totalCount ?? 0;
    return Math.max(1, Math.ceil(count / PAGE_SIZE));
  }, [totalCount]);

  // Keep pagination stable when the total shrinks (e.g. after delete).
  useEffect(() => {
    setPage((prev) => clamp(prev, 1, totalPages));
  }, [totalPages]);

  const refreshHistories = useCallback(async () => {
    // Best-effort refresh to keep table + total count consistent.
    try {
      await Promise.all([refetchHistories(), refetchCount()]);
    } catch {
      // ignore
    }
  }, [refetchCount, refetchHistories]);

  const handleDownloadTemplate = useCallback(() => {
    downloadFile(ESIM_BULK_UPDATE_TEMPLATE, 'esim_bulk_update_template');
    systemAlert.success('Template download started.');
  }, [systemAlert]);

  const handleConfirmFile = useCallback(
    async (file: File) => {
      try {
        const { data, message } = await updateFile(file);

        if (message !== 'OK') {
          systemAlert.error(message || 'Upload failed.');
          return;
        }

        const updateId = Number(data?.[0]);
        if (!Number.isFinite(updateId) || updateId <= 0) {
          systemAlert.error('Upload succeeded but returned an invalid update id.');
          return;
        }

        await refreshHistories();
        navigate(`/stocks/update/${updateId}`);
      } catch {
        systemAlert.error('Upload failed.');
      }
    },
    [navigate, refreshHistories, systemAlert],
  );

  const handleDelete = useCallback(
    async (updateId: number) => {
      const ok = await confirm({
        priority: 'danger',
        title: 'Delete this update run?',
        description: 'This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        autoFocusConfirm: false,
      });
      if (!ok) return;

      try {
        await deleteUpdateHistory(updateId);
        systemAlert.success('Deleted.');
      } catch {
        systemAlert.error('Failed to delete.');
      } finally {
        await refreshHistories();
      }
    },
    [confirm, refreshHistories, systemAlert],
  );

  // Inject page header action (template download only).
  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'download-update-template',
          label: 'Download Update Template',
          startIcon: <PublishedWithChangesIcon />,
          onClick: handleDownloadTemplate,
          variant: 'outlined',
          color: 'warning',
        },
      ],
    });
  }, [handleDownloadTemplate, setPageHeaderSlots]);

  // Columns:
  // #, stockCode, stockName, file, total, ok, fail, status, createdAt, delete
  const columnsCount = 10;

  const head = useMemo(
    () => (
      <TableRow>
        <TableCell align="right" sx={cellSx.nowrap}>
          #
        </TableCell>
        <TableCell sx={cellSx.nowrap}>Stock Code</TableCell>
        <TableCell sx={cellSx.nowrap}>Stock Name</TableCell>
        <TableCell sx={cellSx.nowrap}>File</TableCell>
        <TableCell align="right" sx={cellSx.nowrap}>
          Total
        </TableCell>
        <TableCell align="right" sx={cellSx.nowrap}>
          OK
        </TableCell>
        <TableCell align="right" sx={cellSx.nowrap}>
          Fail
        </TableCell>
        <TableCell sx={cellSx.nowrap}>Status</TableCell>
        <TableCell sx={cellSx.nowrap}>Created At</TableCell>
        <TableCell align="center" sx={cellSx.nowrap}>
          Delete
        </TableCell>
      </TableRow>
    ),
    [],
  );

  const isLoading = Boolean(isHistoriesLoading || isCountLoading);

  return (
    <>
      <Box sx={{ width: '100%', mt: 2 }}>
        <DragDropFile onConfirmFile={handleConfirmFile} />
      </Box>

      {/* Keep spacing consistent with other bulk pages */}
      <Toolbar />

      <Typography variant="subtitle1" fontWeight={800}>
        Update Runs
      </Typography>

      <Box mt={2}>
        <StandardTableToolbar
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount ?? 0}
          onChangePage={(next) => setPage(clamp(next, 1, totalPages))}
        />

        <StandardTableFrame<GetUpdateHistoriesResponse>
          head={head}
          rows={rows}
          getRowKey={(row) => row.esimInventoryBulkUpdateId}
          columnsCount={columnsCount}
          isLoading={isLoading}
          loadingVariant="skeleton"
          emptyText="No update runs found."
          getRowStatus={(row) => (row.isDeleted ? 'inactive' : 'normal')}
          renderRow={(row, index) => {
            const updateId = Number(row.esimInventoryBulkUpdateId);
            const isDeleted = Boolean(row.isDeleted);

            const statusSpec = getStatusChipSpec(row.status, isDeleted);
            const okCount = Number(row.okayCount ?? 0);
            const failCount = Number(row.failedCount ?? 0);
            const total = Number(row.totalCount ?? 0);

            const canNavigate = Number.isFinite(updateId) && updateId > 0;

            return (
              <>
                {/* Navigate ONLY via Index cell (click + keyboard). */}
                <TableCell align="right" sx={cellSx.number}>
                  <Box
                    component="span"
                    role={canNavigate ? 'button' : undefined}
                    tabIndex={canNavigate ? 0 : -1}
                    onClick={() => {
                      if (!canNavigate) return;
                      navigate(`/stocks/update/${updateId}`);
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
                      if (!canNavigate) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/stocks/update/${updateId}`);
                      }
                    }}
                    sx={{ cursor: canNavigate ? 'pointer' : 'default' }}
                  >
                    <Typography
                      variant="body2"
                      color={canNavigate ? 'primary' : 'text.primary'}
                      sx={{
                        display: 'inline',
                        textDecoration: canNavigate ? 'underline' : 'none',
                      }}
                    >
                      {(page - 1) * PAGE_SIZE + index + 1}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{row.stock?.stockCode || '-'}</TableCell>
                <TableCell sx={cellSx.nowrap}>{row.stock?.stockName || '-'}</TableCell>
                <TableCell sx={cellSx.nowrap}>{row.filename || '-'}</TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {total.toLocaleString()}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{ ...cellSx.number, color: 'success.main', fontWeight: 700 }}
                >
                  {okCount.toLocaleString()}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    ...cellSx.number,
                    color: failCount > 0 ? 'error.main' : 'text.secondary',
                    fontWeight: failCount > 0 ? 800 : 600,
                  }}
                >
                  {failCount.toLocaleString()}
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Chip
                    size="small"
                    label={statusSpec.label}
                    color={statusSpec.color}
                    variant={statusSpec.variant}
                    sx={{ fontWeight: 700 }}
                  />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  {row.createdAt ? timestampToDate(row.createdAt) : '-'}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <IconButton
                    size="small"
                    disabled={isDeleted}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDelete(updateId);
                    }}
                    aria-label="delete update history"
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </>
            );
          }}
        />

        <Box mt={2}>
          <StandardPagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount ?? 0}
            onChangePage={(next) => setPage(clamp(next, 1, totalPages))}
          />
        </Box>
      </Box>
    </>
  );
};
